'use client'

import { useMemo, useState } from 'react'
import GfBento from '@/components/common/GfBento'
import { t } from '@/i18n/translations'

/**
 * "Gallery · Field" cities catalog: optional search box + region filter chips + grid of
 * `.ar` cards. Mirrors the reference design's #cities section.
 *
 * `cities` is a pre-mapped array of
 *   { id, name, href, blurb, region, regionValue, area, img }.
 * `regions` is [{ value, label, count }] (only regions that have cities).
 */
export default function GfCitiesGrid({ cities = [], regions = [], lang = 'en', showSearch = true }) {
    const ui = t(lang, 'countryWidgetsUi')
    const [region, setRegion] = useState('all')
    const [query, setQuery] = useState('')

    const shown = useMemo(() => {
        const q = showSearch ? query.trim().toLowerCase() : ''
        return cities.filter((c) => {
            const okReg = region === 'all' || c.regionValue === region
            const okQ =
                !q ||
                (c.name || '').toLowerCase().includes(q) ||
                (c.blurb || '').toLowerCase().includes(q) ||
                (c.region || '').toLowerCase().includes(q)
            return okReg && okQ
        })
    }, [cities, region, query, showSearch])

    return (
        <>
            {showSearch && (
                <div className="cs-search">
                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={ui.cities.search}
                        aria-label={ui.cities.searchLabel}
                    />
                </div>
            )}

            {regions.length > 1 && (
                <div className="gf-ops" role="group" aria-label={ui.cities.regions}>
                    <button
                        type="button"
                        className={`all ${region === 'all' ? 'on' : ''}`}
                        onClick={() => setRegion('all')}
                    >
                        {ui.cities.all}
                    </button>
                    {regions.map((r) => (
                        <button
                            type="button"
                            key={r.value}
                            className={region === r.value ? 'on' : ''}
                            onClick={() => setRegion(r.value)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            )}

            {shown.length > 0
                ? <GfBento items={shown.map((c) => ({
                    id: c.id,
                    href: c.href,
                    img: c.img,
                    alt: c.name,
                    cat: c.region,
                    title: c.name,
                    excerpt: c.blurb,
                    meta: c.area,
                }))} />
                : <div className="cs-empty">{ui.cities.empty}</div>}
        </>
    )
}
